import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { concat, Observable, of } from 'rxjs';
import { catchError, filter, map, mergeMap } from 'rxjs/operators';
import { TenantService } from '.';
import { UserService } from './user.service';
import * as _ from 'lodash';
import { DigestService } from './digest.service';

@Injectable({ providedIn: 'root' })
export class PublicAuthGuard implements CanActivate {
    
    constructor(private digestService: DigestService,
                private userService: UserService, 
                private tenantService: TenantService, 
                private router: Router) { }

    originatorUrlTree(route: ActivatedRouteSnapshot) {
        // So if there were an equivalent of router.navigateByUrl for
        // url tree this all would not be necessary
        // but no joy so on we go

        // build a url to get the fun bits out of it
        const url = new URL(window.location.origin + decodeURIComponent(route.queryParams.originator));
        // we only want the core path (not the path plus we get from originator)
        const targetPath = url.pathname;
        // some iterable magic to turn the searchParams into an object createUrlTree can live with
        const queryParams = Object.fromEntries(Array(...url.searchParams.entries()));
        // we are done with the dirty work so GO GO GO
        return this.router.createUrlTree([targetPath], { queryParams });
    }

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> {
        return this.userService.isLoggedIn()
        .pipe(
            map(status => !!status.user),
            mergeMap(isLoggedIn => isLoggedIn ? of(null) : this.tenantService.getUnauthTenantInfo()),
            mergeMap(tenant => {
                if(!tenant) { // already authenticated
                    return of(route.queryParams.originator ? this.originatorUrlTree(route) : this.router.createUrlTree(['/browse']));
                } else if (tenant.settings?.publicTopics !== 'true' || !route.queryParams.code) {
                    return of(this.router.createUrlTree(['/login'], {queryParams: _.assign({originator: state.url}, state.root.queryParams)}));
                } else { // not logged in and is public and has code
                    return this.userService.verifyCode(route.queryParams.code)
                }
            }),
            catchError(() => of(this.router.createUrlTree(['/browse']))), // stale code
            mergeMap(res => {
                return res.hasOwnProperty('verified') ? 
                    this.userService.isLoggedIn()
                    .pipe(
                        mergeMap(({user}) => this.digestService.getDigestConfigurationsForUser(user.id, true)),
                        mergeMap(() => {
                            if (res.verified && route.queryParams.originator) {
                                return of(this.originatorUrlTree(route));
                            }
                            return of(this.router.createUrlTree(['/browse']));
                        })
                    ) :
                    of(res);
            })
        );
    }
}
